import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from '../../utils/validation';
import { authService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/common/Input';
import { Button } from '../../components/common/Button';
import { AlertCircle, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

type LoginFormData = {
  email: string;
  password: string;
};

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [userId, setUserId] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await authService.login(data);
      if (response.data.requiresMFA) {
        setRequiresMFA(true);
        setUserId(response.data.userId!);
      } else {
        login(response.data.user, response.data.token);
        toast.success('Login successful!');
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMfaError('');
    try {
      const response = await authService.verifyMFA(userId, mfaCode);
      login(response.data.user, response.data.token);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (err: any) {
      setMfaError(err.response?.data?.message || 'Invalid MFA code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-950 via-indigo-800 to-purple-900 relative overflow-hidden items-center justify-center p-12">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 -left-20 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse [animation-delay:1s]" />
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="relative z-10 text-white max-w-md">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 mb-8 text-sm">
            <Sparkles className="h-4 w-4 text-yellow-400" />
            <span className="text-indigo-200">Welcome back</span>
          </div>
          <h1 className="text-4xl font-extrabold mb-4 leading-tight">Your next win is just a ticket away.</h1>
          <p className="text-indigo-200 text-lg leading-relaxed">Sign in to browse raffles, check your tickets, and see if you've won.</p>
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-gradient-to-b from-gray-50 to-white dark:from-gray-950 dark:to-gray-950">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-2xl font-extrabold text-indigo-600">RaffleBowl</h1>
          </div>

          {requiresMFA ? (
            <>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Verify MFA Code</h2>
              <p className="text-gray-500 dark:text-gray-400 mb-8">We've sent a verification code to your email. Enter it below to continue.</p>

              {mfaError && (
                <div className="flex items-center gap-2 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mb-6">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {mfaError}
                </div>
              )}

              <form onSubmit={handleMFASubmit} className="space-y-5">
                <Input label="MFA Code" type="text" value={mfaCode} onChange={(e) => { setMfaCode(e.target.value); setMfaError(''); }} placeholder="Enter 6-digit code" required />
                <Button type="submit" fullWidth isLoading={isLoading} className="py-3 text-base">Verify</Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Sign in</h2>
              <p className="text-gray-500 dark:text-gray-400 mb-8">Enter your credentials to access your account</p>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mb-6">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <Input label="Email" type="email" {...register('email')} error={errors.email?.message} placeholder="your@email.com" />
                <Input label="Password" type="password" {...register('password')} error={errors.password?.message} placeholder="••••••••" />
                <div className="flex justify-end">
                  <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">Forgot password?</Link>
                </div>
                <Button type="submit" fullWidth isLoading={isLoading} className="py-3 text-base">Sign in</Button>
              </form>

              <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
                Don't have an account?{' '}
                <Link to="/register" className="text-indigo-600 hover:text-indigo-700 font-semibold">Create one</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
